import type {
  Conversation,
  CreatorHubConversationChannel,
  DashboardConversationLatestSystemEvent,
} from '@fluenceur/types';
import { ConversationChannelType } from '@fluenceur/types';

export const getConversationReplyChannels = (
  conversation: Conversation
): CreatorHubConversationChannel[] => {
  const { sendMessage } = conversation.capabilities;
  if (!sendMessage.allowed) return [];
  return conversation.channels.filter((channel) => sendMessage.channelTypes.includes(channel.type));
};

export const getDefaultConversationChannel = (
  conversation: Conversation
): CreatorHubConversationChannel | null => {
  const replyChannels = getConversationReplyChannels(conversation);
  const latestChannel = conversation.latestMessage?.channel;
  return (
    (latestChannel?.type === ConversationChannelType.NATIVE_APP &&
      replyChannels.find((channel) => channel.id === latestChannel.id)) ||
    replyChannels.find((channel) => channel.id === conversation.preferredChannel?.id) ||
    replyChannels.find((channel) => channel.id === conversation.currentChannelId) ||
    replyChannels[0] ||
    null
  );
};

/**
 * The send-capability reasons that describe an act of moderation, which is the
 * only vocabulary the banner's "blocked: <reason>" sentence can frame — and the
 * only one the `messages.moderationReason.*` dictionary translates.
 *
 * `channel_unavailable` deliberately stays out: a pure-watch thread has no
 * reply channel, nothing was blocked, and pretending otherwise once put a raw
 * i18n key on screen. A null routes the banner to the plain "messaging is not
 * available" sentence instead.
 */
const MODERATION_FRAMED_CAPABILITY_REASONS = new Set(['archived']);

export const getConversationModerationReason = (conversation: Conversation): string | null => {
  if (conversation.moderation.state === 'blocked') return conversation.moderation.reason;
  if (conversation.capabilities.sendMessage.allowed) return null;

  const reason = conversation.capabilities.sendMessage.reason;
  return reason && MODERATION_FRAMED_CAPABILITY_REASONS.has(reason) ? reason : null;
};

export const isNativeConversation = (conversation: Conversation): boolean =>
  conversation.latestMessage?.channel?.type === ConversationChannelType.NATIVE_APP;

/**
 * What the Inbox row previews, and the date it is stamped with.
 *
 * A thread now carries two independent tips: the last message and the last
 * brand-visible marker. A thread of pure watching has only the second one, so
 * reading `latestMessage` alone dates the row on the thread creation and
 * previews it as empty. Ties go to the message: it is the one that carries
 * words. `event` is only set when a marker won the tie or stands alone —
 * callers branch on its presence rather than on a `kind` discriminant.
 */
export type ConversationPreview = {
  at: string;
  event?: DashboardConversationLatestSystemEvent;
};

const toTimestamp = (value: string | null | undefined): number => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

export const getConversationPreview = (conversation: Conversation): ConversationPreview => {
  const message = conversation.latestMessage;
  const event = conversation.latestSystemEvent;

  if (event && toTimestamp(event.occurredAt) > toTimestamp(message?.sentAt)) {
    return { at: event.occurredAt, event };
  }
  if (message) return { at: message.sentAt };
  return { at: conversation.createdAt };
};
